import { useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  VStack,
  Button,
  Field,
  Input,
  Alert,
  CloseButton
} from "@/storybook-components";
import type { SyntheticEvent, ChangeEvent } from "react";

import {
  setStatus,
  setError,
  setUserEmail
} from "@/redux/features/users/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import { submitEmail } from "@/redux/features/users/thunk";

const Form = (): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.users);
  const [isValid, setIsValid] = useState(true);
  const [isAlertClosed, setIsAlertClosed] = useState(false);
  const [email, setEmail] = useState("");

  const validate = string().email().required();

  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    await dispatch(submitEmail());
  };

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    dispatch(setError({ error: null }));
    const isValid = await validate.isValid(e.target.value);
    dispatch(setStatus({ status: "idle" }));
    setIsAlertClosed(false);
    setIsValid(isValid && e.target.value.length > 0);
    if (isValid) {
      dispatch(setUserEmail({ email: e.target.value }));
    }
  };

  return (
    <VStack my={6} gap={6} as="form" noValidate onSubmit={handleSubmit}>
      <Field.Root invalid={!isValid} disabled={status === "saving"}>
        <Field.Label>{translate("common.email")}</Field.Label>
        <Input
          type="email"
          data-testid="emailInput"
          value={email}
          onChange={handleChange}
        />
        <Field.ErrorText data-testid="emailInputError">
          <Field.ErrorIcon />
          {translate("users.invalidEmail")}
        </Field.ErrorText>
      </Field.Root>
      {(status === "saved" || status === "reject") && !isAlertClosed && (
        <Alert.Root status="success" borderRadius="md" alignItems="flex-start">
          <Alert.Indicator />
          <Alert.Title>{translate("users.resetPasswordInfo")}</Alert.Title>
          <CloseButton
            size="sm"
            color="white"
            _hover={{ bg: "whiteAlpha.300" }}
            _active={{ bg: "whiteAlpha.400" }}
            onClick={() => setIsAlertClosed(true)}
          />
        </Alert.Root>
      )}
      <Button
        variant="primary"
        data-testid="submitButton"
        type="submit"
        width="full"
        loading={status === "saving"}
        disabled={
          !isValid ||
          !email.length ||
          status === "saved" ||
          status === "reject" ||
          isAlertClosed
        }
      >
        {translate("common.submit")}
      </Button>
    </VStack>
  );
};

export default Form;
